import { useId, useState } from "react";

import { passwordChecks } from "../utils/passwordRules";

/**
 * CS-5A — ONE password field with a show/hide control (R-6/R-8). Show/hide is a real button with a changing
 * label and `aria-pressed`, never an icon alone.
 */
export function PasswordInput({ id, label, value, onChange, autoComplete = "new-password", error, describedBy, autoFocus, required = true }) {
  const [shown, setShown] = useState(false);
  const errorId = `${id}-error`;
  const describe = [describedBy, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className="mb-3">
      <label className="form-label" htmlFor={id}>
        {label} {required && <span className="text-danger" aria-hidden="true">*</span>}
      </label>
      <div className="input-group vp-pw-field">
        <input
          id={id}
          type={shown ? "text" : "password"}
          autoComplete={autoComplete}
          className={`form-control ${error ? "is-invalid" : ""}`}
          value={value}
          onChange={onChange}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={describe}
          aria-required={required ? "true" : undefined}
          autoFocus={autoFocus}
          spellCheck={false}
          autoCapitalize="off"
        />
        <button
          type="button"
          className="btn btn-outline-secondary vp-pw-toggle"
          aria-controls={id}
          aria-pressed={shown}
          onClick={() => setShown((v) => !v)}
        >
          <i className={`bi ${shown ? "bi-eye-slash" : "bi-eye"} me-1`} aria-hidden="true" />
          {shown ? "Hide" : "Show"}
          <span className="visually-hidden"> password</span>
        </button>
      </div>
      {error && <div id={errorId} className="invalid-feedback d-block">{error}</div>}
    </div>
  );
}

/**
 * CS-5A — THE new-password block: new password · live requirement checklist · confirm · mismatch. Used by
 * activation, reset, the forced first change and the voluntary change, so the rules read the same everywhere.
 *
 * @param {{ password, confirm, onPassword, onConfirm, email?, errors?, idPrefix? }} props
 * `passwordReady` (utils/passwordRules) reports whether submit may be enabled.
 */
export function NewPasswordFields({ password, confirm, onPassword, onConfirm, email, errors = {}, autoFocus, newLabel = "New password", confirmLabel = "Confirm new password" }) {
  const uid = useId().replace(/:/g, "");
  const checks = passwordChecks(password, { email });
  const listId = `pw-rules-${uid}`;
  const mismatch = confirm.length > 0 && confirm !== password;
  const metCount = checks.filter((c) => c.met).length;

  return (
    <>
      <PasswordInput
        id={`pw-new-${uid}`}
        label={newLabel}
        value={password}
        onChange={onPassword}
        error={errors.newPassword}
        describedBy={listId}
        autoFocus={autoFocus}
      />

      <div className="vp-pw-rules mb-3" id={listId}>
        <div className="small fw-medium mb-1">Your password needs:</div>
        <ul className="list-unstyled small mb-0" role="list">
          {checks.map((c) => (
            <li key={c.key} className={`d-flex align-items-center gap-2 py-1 ${c.met ? "text-success-emphasis" : "text-body-secondary"}`}>
              <i className={`bi ${c.met ? "bi-check-circle-fill" : "bi-circle"}`} aria-hidden="true" />
              <span>{c.label}</span>
              <span className="visually-hidden">{c.met ? "— met" : "— not met"}</span>
              <span className="ms-auto small" aria-hidden="true">{c.met ? "Met" : "Not met"}</span>
            </li>
          ))}
        </ul>
        {/* One polite summary rather than six announcements per keystroke. */}
        <div className="visually-hidden" aria-live="polite">
          {password ? `${metCount} of ${checks.length} password requirements met.` : ""}
        </div>
      </div>

      <PasswordInput
        id={`pw-confirm-${uid}`}
        label={confirmLabel}
        value={confirm}
        onChange={onConfirm}
        error={errors.confirm || (mismatch ? "The passwords do not match" : undefined)}
      />
    </>
  );
}

